<template>
  <div class="admin-page expiring-subscriptions-report">
    <div class="admin-container">
      <div class="admin-header">
        <div class="admin-header__titles">
          <h1 class="admin-title"><i class="fa-solid fa-hourglass-half"></i> Expiring Subscriptions</h1>
          <p class="admin-subtitle">Monitor subscriptions expiring within 30 days and send notifications to users</p>
        </div>
        <div class="admin-header__actions">
          <button class="admin-btn admin-btn--ghost" @click="$router.push('/')">
            <i class="fa-solid fa-house"></i> Go to Site
          </button>
        </div>
      </div>

      <!-- Filter Section -->
      <div class="admin-card admin-section">
        <div class="admin-card__head">
          <h3 class="admin-card__title">Filters &amp; Options</h3>
        </div>
        <div class="filter-controls">
          <!-- Days Input -->
          <div class="days-input-group">
            <label for="daysInput" class="admin-label">Expiring Within (Days):</label>
            <div class="input-wrapper">
              <button @click="decrementDays" class="spinner-btn">−</button>
              <input
                id="daysInput"
                v-model.number="filters.daysUntilExpiry"
                type="number"
                min="1"
                max="365"
                class="days-input"
              />
              <button @click="incrementDays" class="spinner-btn">+</button>
            </div>
          </div>

          <div class="checkbox-group">
            <label class="checkbox-label">
              <input
                type="checkbox"
                v-model="filters.all"
                @change="toggleSelectAll"
              />
              <span>Select All Tiers</span>
            </label>
          </div>

          <div class="checkbox-group tiers-group">
            <h4>Subscription Tiers:</h4>
            <label class="checkbox-label">
              <input
                type="checkbox"
                value="free"
                v-model="filters.selectedTiers"
                :disabled="filters.all"
              />
              <span>Free</span>
            </label>
            <label class="checkbox-label">
              <input
                type="checkbox"
                value="premium"
                v-model="filters.selectedTiers"
                :disabled="filters.all"
              />
              <span>Premium</span>
            </label>
            <label class="checkbox-label">
              <input
                type="checkbox"
                value="business"
                v-model="filters.selectedTiers"
                :disabled="filters.all"
              />
              <span>Business</span>
            </label>
          </div>
        </div>

        <div class="button-group">
          <button @click="fetchReport" class="admin-btn admin-btn--primary" :disabled="loading">
            {{ loading ? 'Loading...' : 'Load Report' }}
          </button>
          <button @click="exportToCSV" class="admin-btn admin-btn--outline" :disabled="items.length === 0">
            Export CSV
          </button>
        </div>
      </div>

      <!-- Status Messages -->
      <div v-if="successMessage" class="admin-badge admin-badge--success admin-section">
        {{ successMessage }}
      </div>
      <div v-if="errorMessage" class="admin-badge admin-badge--danger admin-section">
        {{ errorMessage }}
      </div>

      <!-- Table Section -->
      <div class="admin-card admin-section">
        <div class="admin-card__head">
          <h3 class="admin-card__title">Subscriptions Expiring in {{ filters.daysUntilExpiry }} Days ({{ items.length }} found)</h3>
        </div>

        <!-- Action Buttons for Selected Items -->
        <div v-if="selectedItems.length > 0" class="action-bar">
          <span class="selection-count">{{ selectedItems.length }} selected</span>
          <button @click="sendReminderToSelected" class="admin-btn admin-btn--primary admin-btn--sm" :disabled="sending">
            {{ sending ? 'Sending...' : 'Send Reminder Email' }}
          </button>
          <button @click="expireSelectedUsers" class="admin-btn admin-btn--danger admin-btn--sm" :disabled="sending">
            {{ sending ? 'Processing...' : 'Expire & Send Notification' }}
          </button>
        </div>

        <div v-if="items.length > 0" class="admin-table-wrap admin-table-wrap--scroll">
          <table class="admin-table">
            <thead>
              <tr>
                <th class="checkbox-th">
                  <input
                    type="checkbox"
                    v-model="selectAll"
                    @change="toggleSelectAll"
                    title="Select all subscriptions"
                  />
                </th>
                <th>User</th>
                <th>Plan</th>
                <th>Expires</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in items" :key="item.subscriptionID" class="data-row">
                <td class="checkbox-td">
                  <input
                    type="checkbox"
                    :checked="isSelectedSubscription(item.subscriptionID)"
                    @change="toggleItemSelection(item.subscriptionID)"
                    class="row-checkbox"
                  />
                </td>
                <td class="cell-stack">
                  <span class="cell-stack__main"><UserLink :user-id="item.userID" :label="item.userName" /></span>
                  <span class="cell-stack__sub">{{ item.userEmail }}</span>
                  <span class="cell-stack__sub mono">{{ truncate(item.subscriptionID, 20) }}</span>
                </td>
                <td class="cell-stack">
                  <span class="cell-stack__main">
                    <span class="admin-badge" :class="'badge-' + item.plan">
                      {{ item.plan.toUpperCase() }}
                    </span>
                  </span>
                  <span class="cell-stack__sub">
                    <span class="admin-badge" :class="'status-' + item.status">
                      {{ item.status }}
                    </span>
                  </span>
                </td>
                <td class="cell-stack days-column">
                  <span class="cell-stack__main">
                    <span class="admin-badge" :class="getDaysUntilClass(item.currentPeriodEnd)">
                      in {{ daysUntilExpiry(item.currentPeriodEnd) }} days
                    </span>
                  </span>
                  <span class="cell-stack__sub is-time">{{ formatDate(item.currentPeriodEnd) }}</span>
                </td>
                <td class="actions-td admin-rowactions">
                  <button @click="sendReminder(item)" class="admin-rowbtn" :disabled="sending" title="Send reminder email">
                    <i class="fa-solid fa-envelope"></i><span>Remind</span>
                  </button>
                  <button @click="expireUser(item)" class="admin-rowbtn admin-rowbtn--danger" :disabled="sending" title="Expire subscription and notify user">
                    <i class="fa-solid fa-hourglass-end"></i><span>Expire</span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="admin-empty">
          <i class="fa-solid fa-inbox"></i>
          <p class="admin-empty__title">No expiring subscriptions found with the selected filters.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import axios from '@/utils/axiosinstance';
import UserLink from '@/components/common/UserLink.vue';

const items = ref([]);
const reportSummary = ref(null);
const loading = ref(false);
const sending = ref(false);
const successMessage = ref('');
const errorMessage = ref('');
const selectedSubscriptionIds = ref([]);
const selectAll = ref(false);

const filters = ref({
  all: true,
  selectedTiers: ['free', 'premium', 'business'],
  daysUntilExpiry: 30
});

const selectedItems = computed(() => {
  return items.value.filter(item => selectedSubscriptionIds.value.includes(item.subscriptionID));
});

/**
 * Increment days
 */
const incrementDays = () => {
  if (filters.value.daysUntilExpiry < 365) {
    filters.value.daysUntilExpiry++;
  }
};

/**
 * Decrement days
 */
const decrementDays = () => {
  if (filters.value.daysUntilExpiry > 1) {
    filters.value.daysUntilExpiry--;
  }
};

/**
 * Toggle "Select All" checkbox
 */
const toggleSelectAll = () => {
  if (selectAll.value) {
    selectedSubscriptionIds.value = items.value.map(item => item.subscriptionID);
  } else {
    selectedSubscriptionIds.value = [];
  }
};

/**
 * Handle individual checkbox change
 */
const toggleItemSelection = (subscriptionID) => {
  const index = selectedSubscriptionIds.value.indexOf(subscriptionID);
  if (index > -1) {
    selectedSubscriptionIds.value.splice(index, 1);
  } else {
    selectedSubscriptionIds.value.push(subscriptionID);
  }
  
  // Update select all checkbox state
  selectAll.value = selectedSubscriptionIds.value.length === items.value.length && items.value.length > 0;
};

/**
 * Check if a subscription is selected
 */
const isSelectedSubscription = (subscriptionID) => {
  return selectedSubscriptionIds.value.includes(subscriptionID);
};

/**
 * Fetch the expiring subscriptions report
 */
const fetchReport = async () => {
  loading.value = true;
  errorMessage.value = '';
  successMessage.value = '';
  reportSummary.value = null;
  selectedSubscriptionIds.value = [];
  selectAll.value = false;

  try {
    const token = localStorage.getItem('auth-token');
    const response = await axios.post(
      '/api/admin/expiring-subscriptions',
      {
        filters: {
          tiers: filters.value.all ? ['free', 'premium', 'business'] : filters.value.selectedTiers,
          all: filters.value.all,
          daysUntilExpiry: filters.value.daysUntilExpiry
        }
      },
      token ? { headers: { Authorization: `Bearer ${token}` } } : {}
    );

    if (response.data.success) {
      items.value = response.data.data || [];
      successMessage.value = `Found ${items.value.length} expiring subscriptions`;
    } else {
      errorMessage.value = response.data.message || 'Failed to fetch report';
    }
  } catch (error) {
    errorMessage.value = error.response?.data?.message || error.message || 'Error fetching report';
    console.error('Error fetching expiring subscriptions:', error);
  } finally {
    loading.value = false;
  }
};

/**
 * Send reminder email to a single subscription
 */
const sendReminder = async (item) => {
  const recipientLabel = item.userEmail || item.userName || 'selected user';
  if (!confirm(`Send reminder email to ${recipientLabel}?`)) {
    return;
  }

  sending.value = true;
  errorMessage.value = '';
  successMessage.value = '';

  try {
    const token = localStorage.getItem('auth-token');
    const response = await axios.post(
      '/api/admin/send-reminder',
      {
        subscriptionID: item.subscriptionID,
        userID: item.userID
      },
      token ? { headers: { Authorization: `Bearer ${token}` } } : {}
    );

    if (response.data.success) {
      successMessage.value = `Reminder sent to ${item.userEmail}`;
    } else {
      errorMessage.value = response.data.message || 'Failed to send reminder';
    }
  } catch (error) {
    errorMessage.value = error.response?.data?.message || error.message || 'Error sending reminder';
    console.error('Error sending reminder:', error);
  } finally {
    sending.value = false;
  }
};

/**
 * Send reminder to selected subscription(s)
 */
const sendReminderToSelected = async () => {
  if (selectedItems.value.length === 0) {
    errorMessage.value = 'Please select at least one subscription';
    return;
  }

  if (!confirm(`Send reminder email to ${selectedItems.value.length} user(s)?`)) {
    return;
  }

  sending.value = true;
  errorMessage.value = '';
  successMessage.value = '';

  try {
    const token = localStorage.getItem('auth-token');
    
    for (const item of selectedItems.value) {
      await axios.post(
        '/api/admin/send-reminder',
        {
          subscriptionID: item.subscriptionID,
          userID: item.userID
        },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {}
      );
    }

    successMessage.value = `Reminder sent to ${selectedItems.value.length} user(s)`;
    selectedSubscriptionIds.value = [];
    selectAll.value = false;
  } catch (error) {
    errorMessage.value = error.response?.data?.message || error.message || 'Error sending reminders';
    console.error('Error sending reminders:', error);
  } finally {
    sending.value = false;
  }
};

/**
 * Expire a single user's subscription
 */
const expireUser = async (item) => {
  const recipientLabel = item.userEmail || item.userName || 'selected user';
  if (!confirm(`Expire subscription for ${recipientLabel}? An expiration notification email will be sent.`)) {
    return;
  }

  sending.value = true;
  errorMessage.value = '';
  successMessage.value = '';

  try {
    const token = localStorage.getItem('auth-token');
    const response = await axios.post(
      '/api/admin/expire-subscription',
      {
        subscriptionID: item.subscriptionID,
        userID: item.userID
      },
      token ? { headers: { Authorization: `Bearer ${token}` } } : {}
    );

    if (response.data.success) {
      successMessage.value = `Subscription expired for ${item.userEmail}. Notification email sent.`;
      // Remove from list
      const index = items.value.findIndex(i => i.subscriptionID === item.subscriptionID);
      if (index > -1) {
        items.value.splice(index, 1);
      }
    } else {
      errorMessage.value = response.data.message || 'Failed to expire subscription';
    }
  } catch (error) {
    errorMessage.value = error.response?.data?.message || error.message || 'Error expiring subscription';
    console.error('Error expiring subscription:', error);
  } finally {
    sending.value = false;
  }
};

/**
 * Expire selected user(s) subscription
 */
const expireSelectedUsers = async () => {
  if (selectedItems.value.length === 0) {
    errorMessage.value = 'Please select at least one subscription';
    return;
  }

  if (!confirm(`Expire subscription(s) for ${selectedItems.value.length} user(s)? Notification emails will be sent.`)) {
    return;
  }

  sending.value = true;
  errorMessage.value = '';
  successMessage.value = '';

  try {
    const token = localStorage.getItem('auth-token');
    
    for (const item of selectedItems.value) {
      await axios.post(
        '/api/admin/expire-subscription',
        {
          subscriptionID: item.subscriptionID,
          userID: item.userID
        },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {}
      );
    }

    successMessage.value = `${selectedItems.value.length} subscription(s) expired. Notification emails sent.`;
    
    // Remove from list
    const idsToRemove = selectedItems.value.map(i => i.subscriptionID);
    items.value = items.value.filter(i => !idsToRemove.includes(i.subscriptionID));
    selectedSubscriptionIds.value = [];
    selectAll.value = false;
  } catch (error) {
    errorMessage.value = error.response?.data?.message || error.message || 'Error expiring subscriptions';
    console.error('Error expiring subscriptions:', error);
  } finally {
    sending.value = false;
  }
};

/**
 * Export data to CSV
 */
const exportToCSV = () => {
  if (items.value.length === 0) {
    errorMessage.value = 'No data to export';
    return;
  }

  const headers = ['Subscription ID', 'User Name', 'Email', 'Plan', 'Status', 'Expires', 'Days Until Expiry'];
  const rows = items.value.map(item => [
    item.subscriptionID,
    item.userName,
    item.userEmail,
    item.plan,
    item.status,
    formatDate(item.currentPeriodEnd),
    daysUntilExpiry(item.currentPeriodEnd)
  ]);

  const csv = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
  ].join('\n');

  const blob = new Blob([csv], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `expiring-subscriptions-${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  window.URL.revokeObjectURL(url);
};

/**
 * Calculate days until expiry
 */
const daysUntilExpiry = (expiryDate) => {
  const now = new Date();
  const expiry = new Date(expiryDate);
  const diff = expiry.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

/**
 * Get CSS class for days until expiry
 */
const getDaysUntilClass = (expiryDate) => {
  const days = daysUntilExpiry(expiryDate);
  if (days <= 7) return 'critical';
  if (days <= 14) return 'warning';
  return 'caution';
};

/**
 * Format date for display
 */
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const goBack = () => {
  if (window.history.length > 1) {
    window.history.back();
  } else {
    // fallback to explicit admin route
    window.location.href = '/admin/expiring-subscriptions';
  }
};

/**
 * Truncate text for display
 */
const truncate = (text, length) => {
  if (!text) return '-';
  return text.length > length ? text.substring(0, length) + '...' : text;
};
</script>

<style scoped>
/* Page-specific tweaks only — shared look comes from admin-theme.css */

/* Filter controls */
.filter-controls {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 14px;
}

.days-input-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.days-input-group .admin-label {
  min-width: 150px;
}

.input-wrapper {
  display: flex;
  align-items: center;
  border: 1px solid var(--g-border);
  border-radius: var(--g-radius-sm);
  background: var(--g-surface);
  overflow: hidden;
}

.days-input {
  width: 72px;
  height: 32px;
  padding: 0 8px;
  border: none;
  text-align: center;
  font-size: 14.5px;
  font-weight: 600;
  color: var(--g-text);
  background: var(--g-surface);
}

.days-input:focus {
  outline: none;
  background: var(--g-surface-2);
}

.spinner-btn {
  height: 32px;
  padding: 0 11px;
  background: var(--g-surface-2);
  border: none;
  cursor: pointer;
  font-size: 15.5px;
  font-weight: 700;
  color: var(--g-text-secondary);
  transition: background 0.2s;
}

.spinner-btn:hover {
  background: var(--g-hover);
}

.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.checkbox-group h4 {
  margin: 0 0 8px 0;
  color: var(--g-text-secondary);
  font-size: 12.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .04em;
}

.tiers-group {
  padding-left: 20px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 14px;
  color: var(--g-text);
  user-select: none;
}

.checkbox-label input[type="checkbox"] {
  margin-right: 8px;
  cursor: pointer;
  width: 16px;
  height: 16px;
}

.checkbox-label input[type="checkbox"]:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.button-group {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

/* Action bar */
.action-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 14px;
  background: var(--g-blue-tint);
  border-left: 3px solid var(--g-blue);
  border-radius: var(--g-radius-sm);
  margin-bottom: 12px;
}

.selection-count {
  font-weight: 600;
  color: var(--g-text);
  min-width: 100px;
}

/* Table cells */
.checkbox-th,
.checkbox-td {
  width: 40px;
  text-align: center;
  padding-left: 6px;
  padding-right: 6px;
}

.row-checkbox {
  cursor: pointer;
  width: 16px;
  height: 16px;
}

.mono {
  font-family: 'Courier New', monospace;
  font-size: 12.5px;
  color: var(--g-text-secondary);
}

/* Plan badges */
.badge-free {
  background: var(--g-surface-2);
  color: var(--g-text-secondary);
}

.badge-premium {
  background: var(--g-blue-tint);
  color: var(--g-blue);
}

.badge-business {
  background: var(--g-purple-tint);
  color: var(--g-purple);
}

/* Status badges */
.status-active {
  background: var(--g-green-tint);
  color: var(--g-green);
}

.status-trialing {
  background: var(--g-amber-tint);
  color: var(--g-amber);
}

.status-past_due {
  background: var(--g-red-tint);
  color: var(--g-red);
}

/* Expiry urgency badges */
.days-column .critical {
  background: var(--g-red-tint);
  color: var(--g-red);
}

.days-column .warning {
  background: var(--g-amber-tint);
  color: var(--g-amber);
}

.days-column .caution {
  background: var(--g-green-tint);
  color: var(--g-green);
}

/* This <td> carried its own display:flex on top of .admin-rowactions', which
   is what dropped the row's bottom border under the Actions column. The
   shared td.admin-rowactions rule handles the layout; the cell only needs to
   stay a cell. */
.actions-td {
  white-space: nowrap;
}

@media (max-width: 768px) {
  .button-group {
    flex-direction: column;
  }

  .button-group .admin-btn {
    width: 100%;
  }

  .tiers-group {
    padding-left: 0;
  }

  .action-bar {
    flex-direction: column;
    align-items: flex-start;
  }

  .action-bar button {
    width: 100%;
  }
}
</style>
